import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import apiInstance from '../../../../../Utils/api'
import { Delete } from '../../web_api/api'
import BranchButton from '../shared/BranchButton'
import BranchSearchInput from '../shared/BranchSearchInput'
import BranchImageThumb from '../shared/BranchImageThumb'
import BranchActionIcons from '../shared/BranchActionIcons'
import BranchPagination from '../shared/BranchPagination'
import BranchEmptyState from '../shared/BranchEmptyState'
import BranchBreadcrumb from '../shared/BranchBreadcrumb'
import '../branch.css'

export default function BranchList() {
  const [branchList, setBranchList] = useState([])
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  const abortRef = useRef(null)
  const fetchIdRef = useRef(0)
  const perPage = 10

  const fetchBranches = async (p, kw) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    const id = ++fetchIdRef.current

    setLoading(true)
    try {
      const params = { keyword: kw || '', page: p, limit: perPage }
      if (activeFilter === 'active') params.isActive = true
      if (activeFilter === 'inactive') params.isActive = false
      const res = await apiInstance.get('v2/branches', { params, signal: abortRef.current.signal })
      if (id !== fetchIdRef.current) return
      const data = res.data
      setBranchList(data.list || [])
      setTotalCount(data._metadata?.total_count || 0)
      setPageCount(data._metadata?.page_count || 0)
    } catch (err) {
      if (err?.name === 'CanceledError') return
      console.error(err)
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchBranches(1, keyword)
      setPage(1)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [keyword])

  useEffect(() => {
    fetchBranches(1, keyword)
    setPage(1)
  }, [activeFilter])

  useEffect(() => {
    fetchBranches(page, keyword)
  }, [page, activeFilter])

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This branch will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db2777',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    })
    if (!result.isConfirmed) return
    await Delete('/branch/', id)
    fetchBranches(page, keyword)
  }

  const handleActivate = async (id) => {
    try {
      await apiInstance.post('branch/' + id)
      fetchBranches(page, keyword)
    } catch (err) {
      console.error(err)
    }
  }

  const handleToggle = async (branch) => {
    const action = branch.isActive ? 'deactivate' : 'activate'
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `This branch will be ${action}d.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db2777',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Yes, ${action}`,
      cancelButtonText: 'Cancel'
    })
    if (!result.isConfirmed) return
    try {
      await apiInstance.post(`branch/${branch._id}/${action}`)
      fetchBranches(page, keyword)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <div className='branch-page'>
      <BranchBreadcrumb items={[
        { label: 'Branches', to: '/branch' },
        { label: 'Branch List' }
      ]} />

      <div className='branch-card'>
          <div className='branch-card-header'>
            <div className='branch-filter-group'>
              <button className={'branch-filter-btn' + (activeFilter === '' ? ' active' : '')} onClick={() => setActiveFilter('')}>All</button>
              <button className={'branch-filter-btn' + (activeFilter === 'active' ? ' active' : '')} onClick={() => setActiveFilter('active')}>Active</button>
              <button className={'branch-filter-btn' + (activeFilter === 'inactive' ? ' active' : '')} onClick={() => setActiveFilter('inactive')}>Inactive</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <BranchSearchInput
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (debounceRef.current) clearTimeout(debounceRef.current)
                  fetchBranches(1, keyword)
                  setPage(1)
                }
              }}
              placeholder='Search branches...'
              />
              <BranchButton to='/branch-create'>
                + Add
              </BranchButton>
            </div>
          </div>

        <div className='branch-card-body' style={{ padding: 0 }}>
          {loading ? (
            <div className='branch-loader'>
              <div className='branch-loader-spinner' />
              <div className='branch-loader-text'>Loading branches...</div>
            </div>
          ) : branchList.length === 0 ? (
            <BranchEmptyState />
          ) : (
            <>
              <div className='branch-table-wrapper'>
                <table className='branch-table'>
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '16%' }}>Name</th>
                      <th style={{ width: '18%' }}>Address</th>
                      <th style={{ width: '10%' }}>Phones</th>
                      <th style={{ width: '14%' }}>Hours</th>
                      <th style={{ width: '12%' }}>Closed Days</th>
                      <th style={{ width: '8%' }}>Images</th>
                      <th style={{ width: '8%' }}>Status</th>
                      <th style={{ width: '9%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchList.map((branch, i) => (
                      <tr key={branch._id}>
                        <td>{(page - 1) * perPage + i + 1}</td>
                        <td title={branch.name}>
                          <Link to={'/branch-detail/' + branch._id} style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}>
                            {branch.name}
                          </Link>
                          {branch.displayName && (
                            <div style={{ fontSize: '0.8em', color: '#94a3b8', marginTop: 2 }}>{branch.displayName}</div>
                          )}
                        </td>
                        <td title={branch.address || '-'}>{branch.address || '-'}</td>
                        <td title={branch.phones?.join(', ') || '-'}>
                          {branch.phones?.length > 0
                            ? branch.phones.join(', ')
                            : '-'}
                        </td>
                        <td title={branch.openHoursDisplay || '-'}>{branch.openHoursDisplay || '-'}</td>
                        <td title={branch.closedDays || '-'}>{branch.closedDays || '-'}</td>
                        <td>
                          {branch.imageUrls?.length > 0 ? (
                            <PhotoProvider>
                              {branch.imageUrls.map((url, idx) => (
                                <PhotoView key={idx} src={url}>
                                  {idx === 0 ? (
                                    <BranchImageThumb src={url} alt='' />
                                  ) : (
                                    <span style={{ display: 'none' }} />
                                  )}
                                </PhotoView>
                              ))}
                            </PhotoProvider>
                          ) : (
                            <BranchImageThumb />
                          )}
                        </td>
                        <td>
                          {!branch.isDeleted && (
                            <label className='branch-toggle-sm' title={branch.isActive ? 'Deactivate' : 'Activate'}>
                              <input
                                type='checkbox'
                                checked={!!branch.isActive}
                                onChange={() => handleToggle(branch)}
                              />
                              <span className='branch-toggle-slider' />
                            </label>
                          )}
                        </td>
                        <td>
                          <BranchActionIcons
                            branch={branch}
                            onDelete={handleDelete}
                            onRestore={handleActivate}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <BranchPagination
                page={page}
                pageCount={pageCount}
                totalCount={totalCount}
                perPage={perPage}
                onChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
